/**
 * Reference data for pickers — countries, currencies, and their display names.
 *
 * The country dropdown used to be six hardcoded options, which is why a
 * workspace could only ever be created in one of six countries. It is now
 * driven by the published country-pack catalogue, so the list is whatever the
 * platform actually supports rather than whatever someone typed into a
 * <select> — add a pack and the picker grows on its own.
 *
 * Currencies and display names come from the browser's own ICU tables via
 * Intl. Bundling a currency list would mean shipping a second copy of data
 * every browser already has, and one that goes stale.
 */
import { useMemo } from "react";
import { useApiList } from "./api/hooks";
import { useAuthStore } from "./auth/store";
import { useTranslation } from "./i18n";

export interface CountryOption {
  code: string;
  name: string;
  /**
   * What the country's pack says the workspace should default to. Present
   * only when the API returns it on the pack row: the pack content holds
   * currencyCode, locale and timezone (migration 0031), but
   * GET /v1/localization/country-packs does not include them today, so these
   * are undefined and nothing is defaulted (see WorkspaceCreationPage).
   */
  currencyCode?: string;
  timezone?: string;
  locale?: string;
}

interface CountryPackRow {
  id: string;
  code: string;
  name: string;
  status: string;
  installedVersion: number | null;
  currencyCode?: unknown;
  timezone?: unknown;
  locale?: unknown;
}

/** `country-ae` -> `AE`. Anything else is not a country pack. */
function isoFromPackCode(packCode: string): string | null {
  const match = /^country-([a-z]{2})$/.exec(packCode);
  return match?.[1] ? match[1].toUpperCase() : null;
}

const displayNames = new Map<string, Intl.DisplayNames | null>();

/** Intl.DisplayNames for one language and type, built once. */
function namesIn(
  language: string,
  type: "region" | "currency" | "language",
): Intl.DisplayNames | null {
  const key = `${language}:${type}`;
  if (!displayNames.has(key)) {
    try {
      // "standard" names a locale as language (region) — "English (United
      // States)", not the dialect form "American English" — so every option
      // in a language picker reads the same way.
      displayNames.set(
        key,
        new Intl.DisplayNames([language], {
          type,
          languageDisplay: "standard",
        }),
      );
    } catch {
      displayNames.set(key, null);
    }
  }
  return displayNames.get(key) ?? null;
}

/**
 * Display name for an ISO country code in `language` (English by default),
 * falling back to the code itself.
 */
export function countryName(code: string, language = "en"): string {
  const upper = code.toUpperCase();
  const resolved = namesIn(language, "region")?.of(upper);
  return resolved && resolved !== upper ? resolved : upper;
}

/**
 * Display name for an ISO currency code in `language` (English by default),
 * falling back to the code itself.
 */
export function currencyName(code: string, language = "en"): string {
  const upper = code.toUpperCase();
  const resolved = namesIn(language, "currency")?.of(upper);
  return resolved && resolved !== upper ? resolved : upper;
}

/**
 * Locales a workspace can be created with: the same tags a person can choose
 * in Profile. A country pack's own locale is added by the screen that offers
 * it (WorkspaceCreationPage).
 */
export const LANGUAGE_OPTIONS: readonly string[] = [
  "en-US",
  "ar-EG",
  "ar-SA",
  "ar-AE",
  "fr-FR",
  "de-DE",
  "hi-IN",
];

/**
 * A locale named in its own language — "English (United States)",
 * "العربية (مصر)" — the convention for a language picker, so each person can
 * find theirs whatever the screen's language is.
 */
export function languageName(tag: string): string {
  try {
    return namesIn(tag, "language")?.of(tag) ?? tag;
  } catch {
    return tag;
  }
}

/** A string the pack row carries, or undefined. */
function textField(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * ISO 4217 codes that no country uses as its currency today, so no books may
 * be kept in them, but which the runtime's tables can still list.
 *
 * The runtime list is ICU's "non-deprecated" set, and ICU lags ISO: Node 24
 * (ICU 78) still lists HRK (Croatia moved to the euro on 2023-01-01), SLL
 * (replaced by SLE), ZWL (replaced by ZWG in 2024) and CUC (withdrawn in
 * 2021), next to units of account such as XDR and XSU. Offering them as the
 * currency of a ledger creates books nobody can trade in.
 *
 * - funds codes (ISO 4217 "fund"): BOV, CHE, CHW, CLF, COU, MXV, USN, UYI, UYW
 * - units of account, precious metals, testing and "no currency": XAG, XAU,
 *   XBA, XBB, XBC, XBD, XDR, XPD, XPT, XSU, XTS, XUA, XXX
 * - withdrawn from ISO 4217 and replaced: BYR, CUC, EEK, HRK, LTL, LVL, MRO,
 *   SLL, STD, VEF, ZWL, and ANG (Curaçao and Sint Maarten moved to XCG on
 *   2025-03-31; ANG stopped being legal tender on 2025-07-01, while Chrome
 *   152 and Node 24 still list it)
 *
 * The X-prefixed codes that ARE countries' currencies (XAF, XCD, XCG, XOF,
 * XPF) are kept. A country pack's own currency is offered by the screen even
 * when the runtime does not list it (WorkspaceCreationPage).
 */
export const NOT_A_BOOKS_CURRENCY: ReadonlySet<string> = new Set([
  "BOV",
  "CHE",
  "CHW",
  "CLF",
  "COU",
  "MXV",
  "USN",
  "UYI",
  "UYW",
  "XAG",
  "XAU",
  "XBA",
  "XBB",
  "XBC",
  "XBD",
  "XDR",
  "XPD",
  "XPT",
  "XSU",
  "XTS",
  "XUA",
  "XXX",
  "ANG",
  "BYR",
  "CUC",
  "EEK",
  "HRK",
  "LTL",
  "LVL",
  "MRO",
  "SLL",
  "STD",
  "VEF",
  "ZWL",
]);

/**
 * Current ISO 4217 currencies that replaced a withdrawn code above, which a
 * runtime may not know yet. Chrome 152's trimmed ICU lists none of them (seen
 * 2026-10-11) while it does list SLL and ZWL, so removing the withdrawn code
 * alone left a Sierra Leone or Zimbabwe company no currency to choose.
 *
 * - SLE: Sierra Leone, replaced SLL (2022)
 * - ZWG: Zimbabwe, replaced ZWL (2024)
 * - XCG: Curaçao and Sint Maarten, replaced ANG (2025)
 */
const NEWER_THAN_SOME_RUNTIMES = ["SLE", "XCG", "ZWG"];

/**
 * The currencies a ledger can be kept in, from a runtime's list: without
 * NOT_A_BOOKS_CURRENCY, with the newer codes it may lack, sorted.
 */
export function booksCurrencies(runtime: readonly string[]): string[] {
  const listed = runtime.filter((code) => !NOT_A_BOOKS_CURRENCY.has(code));
  return [...new Set([...listed, ...NEWER_THAN_SOME_RUNTIMES])].sort();
}

/**
 * ISO 4217 currencies a ledger can be kept in: booksCurrencies() of the
 * runtime's own list.
 *
 * `Intl.supportedValuesOf` is unavailable in older engines, so a small
 * trading-currency fallback keeps the picker usable rather than empty.
 */
export const CURRENCY_OPTIONS: string[] = (() => {
  try {
    return booksCurrencies(Intl.supportedValuesOf("currency"));
  } catch {
    return [
      "USD",
      "EUR",
      "GBP",
      "AED",
      "SAR",
      "INR",
      "JPY",
      "CNY",
      "CHF",
      "CAD",
      "AUD",
      "ZAR",
      "BRL",
      "TRY",
    ];
  }
})();

/**
 * Countries offered by the platform, from the published pack catalogue,
 * named and sorted in the interface's language.
 *
 * Returns `isLoading` so a caller can avoid rendering a picker that is
 * briefly empty — an empty country list looks like "no countries supported",
 * which is exactly the wrong impression — and `error` and `retry`, so a
 * failure is shown with its reason and a way to ask again rather than as an
 * empty list.
 *
 * `isLoading` is true until the list has arrived or failed (TanStack's
 * `isPending`), not only while a request is in flight: a retry waiting for
 * the window to regain focus, or a request held while offline, is "paused",
 * and reading that as "loaded, and empty" told people no country existed.
 */
export function useCountryOptions(): {
  options: CountryOption[];
  isLoading: boolean;
  error: Error | null;
  retry: () => void;
} {
  const { i18n } = useTranslation();
  const language = i18n.language;
  // Never asked without a session. Signing out clears the workspace code the
  // query key carries, so the list used to be fetched once more, with no
  // token, on the way out (a 401 seen in the browser after Create).
  const signedIn = useAuthStore((s) => Boolean(s.accessToken));
  const { data, isPending, error, refetch } = useApiList<CountryPackRow>(
    ["reference", "country-packs"],
    "/localization/country-packs",
    { enabled: signedIn },
  );

  const options = useMemo(() => {
    const rows = data?.items ?? [];
    const seen = new Set<string>();
    const result: CountryOption[] = [];
    for (const row of rows) {
      const iso = isoFromPackCode(row.code);
      if (!iso || seen.has(iso)) continue;
      seen.add(iso);
      result.push({
        code: iso,
        name: countryName(iso, language),
        currencyCode: textField(row.currencyCode),
        timezone: textField(row.timezone),
        locale: textField(row.locale),
      });
    }
    return result.sort((a, b) => a.name.localeCompare(b.name, language));
  }, [data, language]);

  return {
    options,
    isLoading: isPending,
    error: error ?? null,
    retry: () => void refetch(),
  };
}

/**
 * A static fallback for the handful of forms that must render before the
 * catalogue loads. Deliberately tiny and deliberately not the primary source:
 * anything user-facing should use useCountryOptions().
 */
export const COUNTRY_OPTIONS: CountryOption[] = [
  "US",
  "GB",
  "DE",
  "FR",
  "AE",
  "SA",
  "IN",
  "CN",
  "JP",
  "BR",
  "ZA",
  "AU",
].map((code) => ({ code, name: countryName(code) }));

/**
 * Every timezone this runtime knows, as {value, label} sorted by name.
 *
 * The profile page offered five hardcoded zones — UTC, New York, Dubai,
 * Berlin, Kolkata — which is a demo, not a product: anyone working outside
 * those five had no way to say where they were, and every timestamp the
 * product showed them was wrong by some number of hours.
 *
 * Sourced from the browser's own ICU tables, which is the same set the API
 * validates against (Node and the browser both resolve IANA names), so the
 * picker and the validator cannot disagree. Bundling a zone list would mean
 * shipping a second copy of data every browser already has, and one that goes
 * stale every time a government moves a DST boundary.
 */
export interface TimezoneOption {
  value: string;
  label: string;
}

/** Current UTC offset for a zone, as +HH:MM — what people recognise. */
function utcOffsetLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    // longOffset yields "GMT+04:00"; "GMT" alone means exactly zero.
    return name === "GMT" ? "+00:00" : name.replace("GMT", "");
  } catch {
    return "";
  }
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = (() => {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  // Older runtimes cannot enumerate zones. Falling back to a short list is
  // better than an empty picker, and the API still accepts any valid zone.
  const zones = supported
    ? supported("timeZone")
    : ["UTC", "America/New_York", "Europe/London", "Asia/Dubai", "Asia/Tokyo"];
  const withUtc = zones.includes("UTC") ? zones : ["UTC", ...zones];
  return withUtc
    .map((value) => {
      const offset = utcOffsetLabel(value);
      return {
        value,
        label: offset ? `${value.replace(/_/g, " ")} (UTC${offset})` : value,
      };
    })
    .sort((a, b) => a.value.localeCompare(b.value));
})();
